import { faCopy } from '@fortawesome/pro-regular-svg-icons';
import { faShieldCheck } from '@fortawesome/pro-solid-svg-icons';
import {
  Alert,
  DialogContent,
  DialogTitle,
  Grid,
  Tooltip,
} from '@mui/material';
import AuthenticatorContext from 'components/Authenticator/AuthenticatorContext';
import useFeedback from 'components/Feedback/useFeedback';
import Loader from 'components/Loading/Loader';
import MfaRecoveryList from 'components/MfaRecovery/MfaRecoveryList';
import { useCreatePendingTotpMutation, useValidatePendingTotpMutation } from 'lib/graphql/graphql';
import { MobileFriendlyMuiOtpInput } from 'modules/Profile/styles';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import QRCode from 'react-qr-code';

import {
  CodeContainer,
  CodeContainerIcon,
  FullWidthContainedButton,
  FullWidthLoadingButton,
  MfaFooter,
  QRCodeContainer,
  RecoveryCodesContainer,
  ShowCodeButton,
  SuccessIcon,
} from './styles';

interface TotpFormProps {
  onClose: () => void
}

function bold(text: React.ReactNode) {
  return <b>{text}</b>;
}

function TotpForm(props: TotpFormProps) {
  const { onClose } = props;
  const { requestRefresh } = useContext(AuthenticatorContext);
  const [step, setStep] = useState<'qr' | 'validation' | 'validated'>('qr');
  const [showCode, setShowCode] = useState<boolean>(false);
  const [codeCopied, setCodeCopied] = useState<boolean>(false);

  const feedback = useFeedback();
  const formControl = useForm({ mode: 'onTouched' });
  const {
    handleSubmit,
  } = formControl;

  const [totpCode, setTotpCode] = useState<string>('');

  const [createPendingTotpMutation, { data, error, loading }] = useCreatePendingTotpMutation();
  const [validatePendingTotpMutation, {
    data: recoveryCodes,
    loading: validating,
    error: validationError,
    reset,
  }] = useValidatePendingTotpMutation();

  useEffect(() => {
    createPendingTotpMutation();
  }, [createPendingTotpMutation]);

  const onSubmit: SubmitHandler<{}> = useCallback(async () => {
    await validatePendingTotpMutation({
      variables: {
        code: totpCode,
      },
    });
    setStep('validated');
    await requestRefresh();
  }, [setStep, validatePendingTotpMutation, totpCode, requestRefresh]);

  useEffect(() => {
    if (error) {
      feedback({
        anchor: { horizontal: 'right', vertical: 'bottom' },
        content: <FormattedMessage id="mfa_setup_qr_error" />,
        severity: 'error',
      });
    }
  }, [error, feedback]);

  const onCodeChange = useCallback((code: string) => {
    reset();
    setTotpCode(code);
  }, [reset]);

  const totpUri = useMemo<string>(() => (
    data?.createPendingTOTP ?? ''
  ), [data]);

  const secret = useMemo<string>(() => {
    if (!totpUri) {
      return '';
    }
    const url = new URL(totpUri);
    return url.searchParams.get('secret') ?? '';
  }, [totpUri]);

  const onCodeCopy = useCallback(() => {
    navigator.clipboard.writeText(secret!);
    setCodeCopied(true);
  }, [secret]);

  return (
    <Loader
      isLoading={loading}
    >
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <FormProvider {...formControl}>
          <DialogTitle>
            <FormattedMessage id="mfa_setup_title" />
          </DialogTitle>
          <DialogContent>
            {step === 'qr' && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormattedMessage id="mfa_setup_qr_description" />
                </Grid>
                <QRCodeContainer item xs={12}>
                  <QRCode value={totpUri!} />
                </QRCodeContainer>
                <Grid item xs={12}>
                  <FormattedMessage id="mfa_setup_qr_alternative" />
                  {showCode ? (
                    <Tooltip
                      title={(
                        <FormattedMessage
                          id={codeCopied ? 'mfa_setup_code_copied' : 'mfa_setup_code_copy'}
                        />
                      )}
                      arrow
                      placement="top"
                      onClose={() => setCodeCopied(false)}
                    >
                      <CodeContainer onClick={onCodeCopy}>
                        <CodeContainerIcon icon={faCopy} />
                        {secret}
                      </CodeContainer>
                    </Tooltip>
                  ) : (
                    <ShowCodeButton onClick={() => setShowCode(true)}>
                      <FormattedMessage id="mfa_setup_qr_alternative_show_code" />
                    </ShowCodeButton>
                  )}
                </Grid>
                <Grid item xs={12} display="flex" justifyContent="center">
                  <FullWidthContainedButton color="secondary" type="button" onClick={() => setStep('validation')}>
                    <FormattedMessage id="mfa_setup_continue" />
                  </FullWidthContainedButton>
                </Grid>
              </Grid>
            )}
            {step === 'validation' && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormattedMessage id="mfa_setup_code_description" />
                </Grid>
                <Grid item xs={12} display="flex" justifyContent="center">
                  <MobileFriendlyMuiOtpInput
                    value={totpCode}
                    sx={{ gap: 1 }}
                    onChange={onCodeChange}
                    length={6}
                  />
                </Grid>
                {validationError && (
                  <Grid item xs={12}>
                    <Alert severity="error">
                      <FormattedMessage id="mfa_setup_code_error" />
                    </Alert>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <FullWidthLoadingButton loading={validating} type="submit" variant="contained" color="secondary">
                    <FormattedMessage id="mfa_setup_validate" />
                  </FullWidthLoadingButton>
                </Grid>
              </Grid>
            )}
            {step === 'validated' && (
              <Grid container spacing={2}>
                <Grid item xs={12} display="flex" justifyContent="center">
                  <Grid container flexDirection="row" spacing={2}>
                    <Grid item xs={12} display="flex" justifyContent="center">
                      <SuccessIcon icon={faShieldCheck} size="6x" />
                    </Grid>
                    <Grid item xs={12} display="flex" justifyContent="center">
                      <FormattedMessage id="mfa_setup_validated" />
                    </Grid>
                  </Grid>
                </Grid>
                <RecoveryCodesContainer item xs={12}>
                  <Grid container flexDirection="column">
                    <Grid item xs={12}>
                      <FormattedMessage id="mfa_setup_validated_recovery_codes" />
                    </Grid>
                    <Grid item xs={12}>
                      <MfaRecoveryList otps={recoveryCodes?.validatePendingTOTP ?? []} />
                    </Grid>
                  </Grid>
                </RecoveryCodesContainer>
                <Grid item xs={12} display="flex" justifyContent="center">
                  <FullWidthContainedButton color="secondary" onClick={onClose}>
                    <FormattedMessage id="mfa_setup_close" />
                  </FullWidthContainedButton>
                </Grid>
              </Grid>
            )}
            <MfaFooter item xs={12}>
              <span>
                <FormattedMessage id="mfa_setup_footer_text" values={{ b: bold }} />
              </span>
            </MfaFooter>
          </DialogContent>
        </FormProvider>
      </form>
    </Loader>
  );
}

export default TotpForm;
